import { CheckCircle } from '@/ui/icons'
import { useTripChecklist } from '@/modules/trips/hooks/useTripChecklist'
import type { ChecklistItemId, TripChecklistTrip } from '@/modules/trips/hooks/useTripChecklist'
import { SectionCard } from './SectionCard'
import { cn } from '@/shared/utils'

const TOTAL_TASKS = 6

export type { ChecklistItemId }

interface TripReadinessSectionProps {
  tripId: string
  trip: TripChecklistTrip
  waypointCount: number
  memberCount: number
  onChecklistItemClick?: (itemId: ChecklistItemId) => void
  className?: string
}

export function TripReadinessSection({
  tripId,
  trip,
  waypointCount,
  memberCount,
  onChecklistItemClick,
  className
}: TripReadinessSectionProps) {
  const { checklist } = useTripChecklist(tripId, trip, { waypointCount, memberCount })
  const completedCount = checklist.filter((item) => item.completed).length
  const progressPercent = (completedCount / TOTAL_TASKS) * 100

  return (
    <SectionCard
      title="Trip Readiness"
      count={completedCount}
      className={cn('min-w-0', className)}
      contentClassName="pt-2"
      fixedHeight
    >
      <div className="flex items-center gap-2 text-sm text-earth-600 dark:text-earth-400 mb-2">
        <div className="flex-1 h-1.5 rounded-full bg-earth-200 dark:bg-earth-700 overflow-hidden">
          <div
            className="h-full bg-adventure-500 rounded-full transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="tabular-nums flex-shrink-0 text-xs">
          {completedCount}/{TOTAL_TASKS}
        </span>
      </div>
      <ul className="space-y-0.5">
        {checklist.map((item) => {
          const isActionable = Boolean(onChecklistItemClick)
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={isActionable ? () => onChecklistItemClick?.(item.id) : undefined}
                disabled={!isActionable}
                className={cn(
                  'w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  isActionable
                    ? 'text-foreground hover:bg-earth-100 dark:hover:bg-earth-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-1'
                    : 'cursor-default'
                )}
                aria-label={
                  isActionable
                    ? item.completed
                      ? `${item.label}, complete. Click to edit.`
                      : `${item.label}, incomplete. Click to set up.`
                    : undefined
                }
              >
                <span className="flex-shrink-0 text-earth-500 dark:text-earth-400">
                  {item.completed ? (
                    <CheckCircle className="h-4 w-4 text-success" />
                  ) : (
                    <span className="h-4 w-4 rounded-full border-2 border-earth-300 dark:border-earth-600 inline-block" />
                  )}
                </span>
                <span className="flex-1 min-w-0 truncate font-medium">{item.label}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </SectionCard>
  )
}
